import SeoFooter from "../components/SeoFooter";
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { 
  Phone, Mail, FileText, CreditCard, Clock, Car, Ban, Users, AlertTriangle, Scale
} from "lucide-react";
import ContactPopup from "../components/ContactPopup";
import PageTransition from "../components/PageTransition";

const TermsPage = () => {
  const sections = [
    {
      icon: CreditCard,
      title: "1. Booking & Payment",
      content: [
        {
          subtitle: "Confirmation",
          text: "A booking is only considered \"Confirmed\" once you receive a confirmation email and a valid payment method is on file."
        },
        {
          subtitle: "Fixed Rates",
          text: "Our quoted prices include GST, tolls, and standard airport parking. Any significant changes to the itinerary (extra stops or route changes) may incur additional charges."
        },
        {
          subtitle: "Payment Methods",
          text: "We accept all major credit cards (Visa, Mastercard, American Express), PayPal, and bank transfers for corporate accounts."
        }
      ]
    },
    {
      icon: AlertTriangle,
      title: "2. Cancellation & Refund Policy",
      content: [
        {
          subtitle: "More than 24 hours notice",
          text: "Full refund or no charge.",
          highlight: "green"
        },
        {
          subtitle: "12–24 hours notice",
          text: "A 50% cancellation fee applies.",
          highlight: "yellow"
        },
        {
          subtitle: "Less than 12 hours notice (or No-Show)",
          text: "100% of the booking fee is non-refundable.",
          highlight: "red"
        }
      ]
    },
    {
      icon: Clock,
      title: "3. Waiting Time & Punctuality",
      content: [
        {
          subtitle: "Airport Pickups",
          text: "We provide 60 minutes of complimentary waiting time for international arrivals and 30 minutes for domestic, starting from the time the flight lands."
        },
        {
          subtitle: "General Pickups",
          text: "For non-airport transfers, a 15-minute grace period is included. After this, waiting time is charged in 15-minute increments at our standard hourly rate."
        },
        {
          subtitle: "Flight Delays",
          text: "We track all flights. There is no charge for delayed flights; however, if a flight is diverted or cancelled, please contact us immediately to avoid a \"No-Show\" charge."
        }
      ]
    },
    {
      icon: Car,
      title: "4. Vehicle Conduct",
      content: [
        {
          subtitle: "Strict No-Smoking Policy",
          text: "Smoking or vaping is strictly prohibited in all vehicles. A cleaning fee of $250 AUD will apply for any violations or excessive cleaning required (e.g., spills, sickness).",
          highlight: "red"
        },
        {
          subtitle: "Passenger Capacity",
          text: "For safety and insurance reasons, the number of passengers must not exceed the vehicle's licensed capacity."
        },
        {
          subtitle: "Luggage",
          text: "Standard luggage is included. Oversized items or excessive luggage may require a larger vehicle at additional cost."
        }
      ]
    },
    {
      icon: Users,
      title: "5. Passenger Responsibilities",
      content: [
        {
          subtitle: "Accurate Information",
          text: "Passengers must provide accurate pickup addresses, contact details, and flight information at the time of booking."
        },
        {
          subtitle: "Child Safety",
          text: "Appropriate child restraints are required by law. Please request child seats at the time of booking."
        },
        {
          subtitle: "Conduct",
          text: "Passengers are expected to treat chauffeurs with respect. We reserve the right to terminate a trip if passenger behavior is deemed unsafe or inappropriate."
        }
      ]
    },
    {
      icon: Scale,
      title: "6. Liability & Insurance",
      content: [
        {
          subtitle: "Insurance Coverage",
          text: "All vehicles are fully insured for passenger liability. However, LimoNearMe is not liable for loss or damage to personal belongings left in vehicles."
        },
        {
          subtitle: "Force Majeure",
          text: "LimoNearMe shall not be liable for delays or cancellations caused by circumstances beyond our control, including but not limited to natural disasters, severe weather, or government actions."
        },
        {
          subtitle: "Governing Law",
          text: "These terms and conditions are governed by and construed in accordance with the laws of Queensland, Australia. Any disputes arising shall be subject to the exclusive jurisdiction of the courts of Queensland."
        }
      ]
    }
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Terms & Conditions - Limo Near Me",
    "description": "Terms and conditions for LimoNearMe chauffeur and limousine services. Booking, payment, cancellation policy, waiting times, and vehicle conduct rules.",
    "publisher": {
      "@type": "Organization",
      "name": "Limo Near Me",
      "url": "https://www.limonearme.com.au"
    }
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Terms & Conditions | Limo Near Me Australia</title>
          <meta name="description" content="LimoNearMe terms and conditions. Booking confirmation, cancellation policy, waiting times, vehicle conduct rules, and passenger responsibilities for chauffeur services in Australia." />
          <meta name="keywords" content="limo terms conditions, limousine cancellation policy, chauffeur service terms, booking terms Australia" />
          <link rel="canonical" href="https://www.limonearme.com.au/terms" />
          <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        </Helmet>

        <ContactPopup />

        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-black/5">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center">
                <img src="/limo-logo.jpg" alt="Limo Near Me" className="h-10 w-auto" />
              </Link>
              <div className="hidden md:flex items-center gap-6">
                <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Home</Link>
                <Link to="/fleet" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Our Fleet</Link>
                <Link to="/about" className="text-sm text-muted-foreground hover:text-foreground transition-colors">About Us</Link>
                <Link to="/contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Contact</Link>
                <a href="tel:+61481437772" className="flex items-center gap-2 text-primary font-semibold">
                  <Phone className="h-4 w-4" />
                  +61 481 437 772
                </a>
                <Link to="/book">
                  <Button size="sm">Book Now</Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="pt-24 pb-12 bg-gradient-to-b from-gray-900 to-gray-800 text-white">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <FileText className="h-16 w-16 mx-auto mb-6 text-white/80" />
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Terms & Conditions
              </h1>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Please read these terms carefully before booking with LimoNearMe.
              </p>
              <p className="text-sm text-gray-400 mt-4">
                Last updated: February 2026
              </p>
            </motion.div>
          </div>
        </section>

        {/* Quick Summary */}
        <section className="py-8 bg-amber-50 border-b border-amber-200">
          <div className="max-w-4xl mx-auto px-4">
            <div className="flex items-start gap-4">
              <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h2 className="font-semibold text-amber-900 mb-2">Important Cancellation Notice</h2>
                <p className="text-amber-800 text-sm">
                  <strong>24+ hours:</strong> Full refund • <strong>12-24 hours:</strong> 50% fee • <strong>&lt;12 hours/No-show:</strong> Non-refundable
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Terms Content */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            {sections.map((section, sectionIndex) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: sectionIndex * 0.1 }}
                className="mb-12"
              >
                {/* Section Header */}
                <div className="flex items-center gap-4 mb-6 pb-4 border-b border-black/10">
                  <div className="w-12 h-12 rounded-full bg-gray-900 flex items-center justify-center">
                    <section.icon className="h-6 w-6 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-foreground">{section.title}</h2>
                </div>

                {/* Section Content */}
                <div className="space-y-6 pl-16">
                  {section.content.map((item, itemIndex) => (
                    <div key={itemIndex} className={`${item.highlight ? 'p-4 rounded-lg' : ''} ${
                      item.highlight === 'green' ? 'bg-green-50 border-l-4 border-green-500' :
                      item.highlight === 'yellow' ? 'bg-yellow-50 border-l-4 border-yellow-500' :
                      item.highlight === 'red' ? 'bg-red-50 border-l-4 border-red-500' : ''
                    }`}>
                      <h3 className="font-semibold text-foreground mb-2">{item.subtitle}</h3>
                      <p className="text-muted-foreground">{item.text}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}

            {/* Governing Law Notice */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-12 p-6 bg-gray-100 rounded-xl"
            >
              <div className="flex items-start gap-4">
                <Scale className="h-6 w-6 text-gray-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Legal Jurisdiction</h3>
                  <p className="text-muted-foreground text-sm">
                    These terms and conditions are governed by the laws of Queensland, Australia. 
                    By using our services, you agree to submit to the exclusive jurisdiction of the courts in Queensland 
                    for the resolution of any disputes.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Agreement Notice */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-8 p-6 bg-primary/5 rounded-xl border border-primary/20"
            >
              <p className="text-center text-foreground">
                By making a booking with LimoNearMe, you acknowledge that you have read, understood, 
                and agree to be bound by these Terms & Conditions.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold mb-4">Have Questions About Our Terms?</h2>
            <p className="text-muted-foreground mb-6">
              Our team is happy to clarify any policies before you book.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:+61481437772">
                <Button size="lg" className="rounded-full px-8">
                  <Phone className="h-4 w-4 mr-2" />
                  Call +61 481 437 772
                </Button>
              </a>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="rounded-full px-8">
                  <Mail className="h-4 w-4 mr-2" />
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
      <SeoFooter />
      </div>
    </PageTransition>
  );
};

export default TermsPage;
